import type { Site } from "@/shared/model/user/UserDomains.ts";
import { useCallback, useEffect, useState } from "react";
import { ScheduleReferenceData } from "@/pages/human-resources/time-management/business/reference/ScheduleReferenceData.ts";
import loadSiteScheduleReferenceData from "@/pages/human-resources/time-management/endpoints/reference/site/SiteScheduleReferenceDataLoader.ts";
import loadDomainScheduleReferenceData from "@/pages/human-resources/time-management/endpoints/reference/domain/DomainScheduleReferenceDataLoader.ts";
import { mapToSiteScheduleReferenceData } from "@/pages/human-resources/time-management/endpoints/reference/site/inout/SiteScheduleReferenceDataOut.ts";
import { mapToDomainScheduleReferenceData } from "@/pages/human-resources/time-management/endpoints/reference/domain/inout/DomainScheduleReferenceDataOut.ts";
import useDefinedContextValue from "@/shared/custom-hook/useDefinedContextValue.ts";
import {
  ScheduleReferenceDataStateContext,
  ScheduleReferenceDataStateDispatchContext,
} from "@/pages/human-resources/time-management/schedules/reference-provider/ScheduleReferenceDataStateProvider.tsx";
import useScheduleDataStateDispatch from "@/pages/human-resources/time-management/schedules/site-schedule/model/schedule-data/useScheduleDataStateDispatch.ts";
import { getSiteScheduleTemplates } from "@/pages/human-resources/time-management/endpoints/schedules/templates/ScheduleTemplatesEndpoints.ts";
import { templateMapper } from "@/pages/human-resources/time-management/endpoints/schedules/templates/inout/ScheduleTemplateOut.ts";
import { SiteScheduleReferenceData } from "@/pages/human-resources/time-management/business/reference/SiteScheduleReferenceData.ts";
import type { ScheduleTemplate } from "@/pages/human-resources/time-management/business/schedule-template/ScheduleTemplate.ts";
import useScheduleViewFilters from "@/pages/human-resources/time-management/components/display-filters/provider/useScheduleViewFilters.ts";
import { FilteredOutEntities } from "@/pages/human-resources/time-management/components/display-filters/model/FilteredOutEntities.ts";

export default function useScheduleReferenceDataLoader(
  currentSite: Site | undefined,
): ScheduleReferenceData | undefined {
  const refDataState = useDefinedContextValue(
    ScheduleReferenceDataStateContext,
  );
  const refDataStateDispatch = useDefinedContextValue(
    ScheduleReferenceDataStateDispatchContext,
  );

  const scheduleDataStateDispatch = useScheduleDataStateDispatch();
  const { dispatch: viewFiltersDispatch } = useScheduleViewFilters();

  const [referenceData, setReferenceData] = useState<
    ScheduleReferenceData | undefined
  >(undefined);

  const handlePromisedRefData = useCallback(
    async (
      promisedRefData: Promise<ScheduleReferenceData>,
      settledAction: "referenceDataSettled" | "siteScheduleTemplatesSettled",
    ) => {
      let refData: ScheduleReferenceData | undefined = undefined;
      try {
        refData = await promisedRefData;
      } catch (error: unknown) {
        console.error(error);
      } finally {
        setReferenceData(refData);
        refDataStateDispatch({
          type: settledAction,
          errorOccurred: refData === undefined,
        });
      }
      return refData;
    },
    [refDataStateDispatch],
  );

  useEffect(() => {
    if (currentSite === undefined) return;
    if (refDataState.site !== currentSite) {
      refDataStateDispatch({
        type: "referenceDataChangeSite",
        site: currentSite,
      });
    } else if (refDataState.referenceDataAreStale) {
      void handlePromisedRefData(
        updatedReferenceData(refDataState.site, referenceData),
        "referenceDataSettled",
      ).then((refData) => {
        scheduleDataStateDispatch({ type: "contractsAndSchedulesMarkStale" });
        viewFiltersDispatch({
          type: "initFilters",
          filteredOutEntities: FilteredOutEntities.createFromFavoriteSections(
            refData?.domainData.sections ?? [],
          ),
        });
        return;
      });
    } else if (
      referenceData !== undefined &&
      refDataState.siteScheduleTemplatesAreStale
    ) {
      void handlePromisedRefData(
        referenceDataWithFreshSiteScheduleTemplates(referenceData),
        "siteScheduleTemplatesSettled",
      );
    }
  }, [
    currentSite,
    handlePromisedRefData,
    referenceData,
    refDataState.referenceDataAreStale,
    refDataState.site,
    refDataState.siteScheduleTemplatesAreStale,
    refDataStateDispatch,
    scheduleDataStateDispatch,
    viewFiltersDispatch,
  ]);
  return referenceData;
}

async function referenceDataWithFreshSiteScheduleTemplates(
  currentData: ScheduleReferenceData,
): Promise<ScheduleReferenceData> {
  const siteScheduleTemplates = await loadSiteScheduleTemplates(currentData);
  return new ScheduleReferenceData(
    currentData.domainData,
    new SiteScheduleReferenceData(
      currentData.siteData.siteId,
      currentData.siteData.preferences,
      currentData.siteData.siteAddress,
      siteScheduleTemplates,
      currentData.siteData.dailyTags,
      currentData.siteData.availableIndicators,
    ),
  );
}

async function updatedReferenceData(
  currentSite: Site,
  currentData: ScheduleReferenceData | undefined,
): Promise<ScheduleReferenceData> {
  if (currentData?.domainData.domainId !== currentSite.domainId) {
    return loadAllReferenceData(currentSite);
  } else if (currentData.siteData.siteId !== currentSite.id) {
    const siteDataOut = await loadSiteScheduleReferenceData(
      currentSite.domainId,
      currentSite.id,
    );
    return new ScheduleReferenceData(
      currentData.domainData,
      mapToSiteScheduleReferenceData(
        currentSite,
        currentData.domainData,
        siteDataOut,
      ),
    );
  } else {
    return currentData;
  }
}

async function loadAllReferenceData(
  site: Site,
): Promise<ScheduleReferenceData> {
  const [domainDataOut, siteDataOut] = await Promise.all([
    loadDomainScheduleReferenceData(site.domainId),
    loadSiteScheduleReferenceData(site.domainId, site.id),
  ]);
  const domainData = mapToDomainScheduleReferenceData(domainDataOut);
  return new ScheduleReferenceData(
    domainData,
    mapToSiteScheduleReferenceData(site, domainData, siteDataOut),
  );
}

async function loadSiteScheduleTemplates(
  currentData: ScheduleReferenceData,
): Promise<ScheduleTemplate[]> {
  const {
    domainData: { domainId },
    siteData: { siteId },
  } = currentData;
  return (await getSiteScheduleTemplates(domainId, siteId)).map(
    templateMapper(currentData.domainData.hourlyTagsResolver, domainId, siteId),
  );
}
